import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const [BLUE, ORANGE, GREEN] = CHART_COLORS;

function Text({
  x,
  y,
  children,
  anchor = "middle",
  italic = true,
  color = CHART_INK.text,
}: {
  x: number;
  y: number;
  children: string;
  anchor?: "start" | "middle" | "end";
  italic?: boolean;
  color?: string;
}) {
  return (
    <text
      fill={color}
      fontSize="14"
      fontStyle={italic ? "italic" : "normal"}
      textAnchor={anchor}
      x={x}
      y={y}
    >
      {children}
    </text>
  );
}

/* ── Force against extension ──────────────────────────────────────────────── */

const G = { left: 70, right: 520, top: 24, bottom: 196 };

/** A straight F–x line from the origin to the working point, with the energy triangle. */
export function HookeGraph({
  id,
  label,
  description,
  extensionText,
  forceText,
  energyText,
  /** Fraction of the x range (0–1) at which the point sits; the axes are not numbered. */
  stretched = true,
}: {
  id: string;
  label: string;
  description: string;
  extensionText: string;
  forceText: string;
  energyText: string;
  stretched?: boolean;
}) {
  const px = G.left + (G.right - G.left) * 0.62;
  const py = G.bottom - (G.bottom - G.top) * 0.62;
  return (
    <ChartSvg
      description={description}
      height={236}
      id={id}
      label={label}
      width={560}
    >
      <polygon
        fill={BLUE}
        fillOpacity="0.18"
        points={`${G.left},${G.bottom} ${px},${G.bottom} ${px},${py}`}
      />
      <line
        stroke={CHART_INK.axis}
        x1={G.left}
        x2={G.right}
        y1={G.bottom}
        y2={G.bottom}
      />
      <line
        stroke={CHART_INK.axis}
        x1={G.left}
        x2={G.left}
        y1={G.top}
        y2={G.bottom}
      />
      <line
        stroke={BLUE}
        strokeWidth="3"
        x1={G.left}
        x2={G.right - 40}
        y1={G.bottom}
        y2={G.top + 24}
      />
      <line
        stroke={CHART_INK.muted}
        strokeDasharray="4 3"
        x1={px}
        x2={px}
        y1={py}
        y2={G.bottom}
      />
      <line
        stroke={CHART_INK.muted}
        strokeDasharray="4 3"
        x1={G.left}
        x2={px}
        y1={py}
        y2={py}
      />
      <circle cx={px} cy={py} fill={ORANGE} r={6} />
      <Text x={px} y={G.bottom + 20}>
        {extensionText}
      </Text>
      <Text anchor="end" x={G.left - 8} y={py + 4}>
        {forceText}
      </Text>
      <Text x={G.right} y={G.bottom + 20}>
        {stretched ? "x" : "−x"}
      </Text>
      <Text x={G.left} y={G.top - 6}>
        F
      </Text>
      <Text
        color={CHART_INK.text}
        italic={false}
        x={(G.left + px) / 2 + 30}
        y={G.bottom - 12}
      >
        {energyText}
      </Text>
    </ChartSvg>
  );
}

/* ── Springs in series or parallel ────────────────────────────────────────── */

function zigzag(x1: number, x2: number, y: number, coils = 6, amp = 9): string {
  const step = (x2 - x1) / (coils * 2 + 2);
  let d = `M ${x1} ${y} L ${x1 + step} ${y}`;
  for (let i = 0; i < coils * 2; i++) {
    const x = x1 + step * (i + 1.5);
    d += ` L ${x} ${y + (i % 2 === 0 ? -amp : amp)}`;
  }
  return `${d} L ${x2 - step} ${y} L ${x2} ${y}`;
}

const SUB = ["₁", "₂", "₃", "₄", "₅", "₆"];

/** n springs drawn between a wall and a load, end to end or side by side. */
export function ComboDiagram({
  id,
  label,
  description,
  count,
  arrangement,
  equivalentText,
}: {
  id: string;
  label: string;
  description: string;
  count: number;
  arrangement: "series" | "parallel";
  equivalentText: string;
}) {
  const n = Math.min(Math.max(count, 1), 4);
  const names = Array.from({ length: n }, (_, i) => `k${SUB[i]}`);
  if (arrangement === "parallel") {
    const height = 40 + n * 44 + 30;
    const ys = names.map((_, i) => 40 + i * 44);
    return (
      <ChartSvg
        description={description}
        height={height}
        id={id}
        label={label}
        width={560}
      >
        <line
          stroke={CHART_INK.text}
          strokeWidth="4"
          x1={90}
          x2={90}
          y1={22}
          y2={ys[n - 1] + 22}
        />
        <line
          stroke={CHART_INK.text}
          strokeWidth="4"
          x1={400}
          x2={400}
          y1={22}
          y2={ys[n - 1] + 22}
        />
        {ys.map((y, i) => (
          <g key={names[i]}>
            <path
              d={zigzag(90, 400, y)}
              fill="none"
              stroke={BLUE}
              strokeWidth="2"
            />
            <Text x={245} y={y - 14}>
              {names[i]}
            </Text>
          </g>
        ))}
        <line
          markerEnd="none"
          stroke={ORANGE}
          strokeWidth="3"
          x1={400}
          x2={470}
          y1={(ys[0] + ys[n - 1]) / 2}
          y2={(ys[0] + ys[n - 1]) / 2}
        />
        <Text
          anchor="start"
          color={ORANGE}
          x={478}
          y={(ys[0] + ys[n - 1]) / 2 + 5}
        >
          F
        </Text>
        <Text anchor="start" x={90} y={height - 8}>
          {equivalentText}
        </Text>
      </ChartSvg>
    );
  }
  const segment = 380 / n;
  return (
    <ChartSvg
      description={description}
      height={130}
      id={id}
      label={label}
      width={560}
    >
      <line
        stroke={CHART_INK.text}
        strokeWidth="4"
        x1={50}
        x2={50}
        y1={30}
        y2={80}
      />
      {names.map((name, i) => (
        <g key={name}>
          <path
            d={zigzag(50 + i * segment, 50 + (i + 1) * segment, 55, 4)}
            fill="none"
            stroke={GREEN}
            strokeWidth="2"
          />
          <Text x={50 + i * segment + segment / 2} y={36}>
            {name}
          </Text>
        </g>
      ))}
      <line stroke={ORANGE} strokeWidth="3" x1={430} x2={500} y1={55} y2={55} />
      <Text anchor="start" color={ORANGE} x={508} y={60}>
        F
      </Text>
      <Text anchor="start" x={50} y={118}>
        {equivalentText}
      </Text>
    </ChartSvg>
  );
}

/* ── Displacement against time ────────────────────────────────────────────── */

const O = { left: 56, right: 530, mid: 100, amp: 62 };

/** x = A cos(2πt/T) over two periods, with A and T marked. */
export function OscillationGraph({
  id,
  label,
  description,
  amplitudeText,
  periodText,
}: {
  id: string;
  label: string;
  description: string;
  amplitudeText: string;
  periodText: string;
}) {
  const width = O.right - O.left;
  const points = Array.from({ length: 121 }, (_, i) => {
    const t = (i / 120) * 2;
    return `${(O.left + (t / 2) * width).toFixed(1)},${(O.mid - O.amp * Math.cos(2 * Math.PI * t)).toFixed(1)}`;
  }).join(" ");
  const period = width / 2;
  return (
    <ChartSvg
      description={description}
      height={214}
      id={id}
      label={label}
      width={560}
    >
      <line
        stroke={CHART_INK.axis}
        x1={O.left}
        x2={O.right}
        y1={O.mid}
        y2={O.mid}
      />
      <line
        stroke={CHART_INK.axis}
        x1={O.left}
        x2={O.left}
        y1={O.mid - O.amp - 14}
        y2={O.mid + O.amp + 14}
      />
      <polyline fill="none" points={points} stroke={BLUE} strokeWidth="3" />
      <line
        stroke={CHART_INK.muted}
        strokeDasharray="4 3"
        x1={O.left}
        x2={O.right}
        y1={O.mid - O.amp}
        y2={O.mid - O.amp}
      />
      <Text anchor="end" x={O.left - 8} y={O.mid - O.amp + 5}>
        {amplitudeText}
      </Text>
      <Text anchor="end" x={O.left - 8} y={O.mid + 5}>
        0
      </Text>
      <line
        markerEnd="none"
        stroke={ORANGE}
        strokeWidth="2.5"
        x1={O.left}
        x2={O.left + period}
        y1={O.mid + O.amp + 34}
        y2={O.mid + O.amp + 34}
      />
      <line
        stroke={ORANGE}
        strokeWidth="2.5"
        x1={O.left}
        x2={O.left}
        y1={O.mid + O.amp + 28}
        y2={O.mid + O.amp + 40}
      />
      <line
        stroke={ORANGE}
        strokeWidth="2.5"
        x1={O.left + period}
        x2={O.left + period}
        y1={O.mid + O.amp + 28}
        y2={O.mid + O.amp + 40}
      />
      <Text color={ORANGE} x={O.left + period / 2} y={O.mid + O.amp + 56}>
        {periodText}
      </Text>
      <Text anchor="start" x={O.left + 10} y={O.mid - O.amp - 18}>
        x
      </Text>
      <Text anchor="end" x={O.right} y={O.mid - 8}>
        t
      </Text>
    </ChartSvg>
  );
}
